import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { ArrowRight, Sparkles, Volume2, VolumeX } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { useLanguage } from "@/i18n/LanguageContext";

const fadeUp = {
  hidden: { y: 30, opacity: 0 },
  show: (i = 0) => ({ y: 0, opacity: 1, transition: { duration: 0.8, delay: 0.1 * i, ease: "easeOut" } }),
};

export default function Hero() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const { t, lang } = useLanguage();

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    v.play().catch(() => {});
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden pt-32 pb-20"
    >
      {/* Background image */}
      <img
        src={BRAND.hero}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover object-center opacity-60"
      />
      {/* Gradients */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to top, #0A0A0A 5%, rgba(10,10,10,0.85) 40%, rgba(10,10,10,0.4) 75%, rgba(10,10,10,0.65) 100%)",
        }}
      />
      {/* Color glows */}
      <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-[#E6007E]/25 blur-[140px]" />
      <div className="absolute top-20 right-0 w-[480px] h-[480px] rounded-full bg-[#00A6E6]/20 blur-[140px]" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0}
            className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 mb-8"
          >
            <Sparkles size={14} className="text-[#8BC53F]" />
            <span className="text-xs tracking-[0.18em] uppercase text-zinc-300">
              {t.hero.badge}
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            data-testid="hero-headline"
            className="font-display text-[7vw] sm:text-5xl md:text-3xl lg:text-5xl xl:text-6xl font-light leading-[1.08] tracking-tighter"
          >
            {lang === "ar" ? (
              <>
                <span className="whitespace-nowrap">
                  اقلب <span className="italic text-gradient-brand font-medium">صفحةً جديدة</span>،
                </span>
                <br />
                <span className="whitespace-nowrap">وابعث الحياة في كل صفحة.</span>
              </>
            ) : (
              <>
                <span className="whitespace-nowrap">
                  Turn a{" "}
                  <span className="italic text-gradient-brand font-medium inline-block pr-3 md:pr-5">new leaf</span>
                  <span className="-ml-3 md:-ml-5">,</span>
                </span>
                <br />
                <span className="whitespace-nowrap">bring every page to life.</span>
              </>
            )}
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-8 max-w-2xl text-lg md:text-xl text-zinc-300 leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#download"
              data-testid="hero-cta-download"
              className="group inline-flex items-center gap-2 rounded-full bg-white text-black px-7 py-4 font-medium hover:bg-zinc-200 transition-colors duration-200"
            >
              {t.hero.cta1}
              <ArrowRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1"
              />
            </a>
            <a
              href="#showcase"
              data-testid="hero-cta-explore"
              className="inline-flex items-center gap-2 rounded-full glass px-7 py-4 text-zinc-100 hover:bg-white/10 transition-colors duration-200"
            >
              {t.hero.cta2}
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-14 flex items-center gap-8 text-xs text-zinc-500 tracking-widest uppercase"
          >
            <span>{t.hero.ticker[0]}</span>
            <span className="h-px w-8 bg-zinc-700" />
            <span>{t.hero.ticker[1]}</span>
            <span className="h-px w-8 bg-zinc-700 hidden sm:block" />
            <span className="hidden sm:inline">{t.hero.ticker[2]}</span>
          </motion.div>
        </div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={5}
          className="md:col-span-4 block space-y-6 mt-10 md:mt-0"
        >
          {/* Free-standing iPhone demo — matches the featured case study style */}
          <div
            data-testid="hero-video-frame"
            className="relative flex justify-center"
          >
            <div className="absolute -inset-6 rounded-[40px] bg-[#6E2C9A]/25 blur-3xl pointer-events-none z-0" />
            <div className="relative w-full max-w-[280px] rounded-[34px] overflow-hidden" style={{ aspectRatio: "440 / 862" }}>
              <video
                ref={videoRef}
                src="/hero/hero-iphone-full.mp4?v=5"
                poster="/hero/hero-iphone-poster.jpg?v=5"
                autoPlay
                muted={muted}
                loop
                playsInline
                preload="metadata"
                aria-label="Paigez.ai interactive digital magazine demo"
                className="relative z-[1] h-full w-full object-cover rounded-[34px]"
              />
              <button
                type="button"
                onClick={toggleMute}
                data-testid="hero-video-mute-toggle"
                aria-label={muted ? "Unmute video" : "Mute video"}
                className="absolute bottom-4 right-4 z-10 inline-flex items-center justify-center h-10 w-10 rounded-full bg-black/55 backdrop-blur border border-white/15 text-white hover:bg-black/75 transition-colors"
              >
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
            </div>
          </div>

          <div className="glass rounded-3xl p-6 relative overflow-hidden">
            <div className="absolute -top-16 -right-10 w-40 h-40 rounded-full bg-[#F7941D]/30 blur-3xl" />
            <p className="font-display italic text-2xl leading-snug text-zinc-100">
              {t.hero.quote}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#E6007E] to-[#6E2C9A]" />
              <div>
                <p className="text-sm text-zinc-100">{t.hero.quoteWho}</p>
                <p className="text-xs text-zinc-500">Paigez.ai</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
